package one.healthium.notifications.manager

import io.ktor.client.HttpClient
import io.ktor.client.request.get
import io.ktor.client.statement.bodyAsBytes
import io.ktor.http.HttpHeaders
import io.ktor.http.Url
import io.ktor.http.isSuccess
import okio.ByteString.Companion.encodeUtf8

/**
 * Fetches an image from the given URL using the supplied [HttpClient] and persists it
 * via the provided streaming [fileSaver] lambda.
 */
class ImageFetcher(
    private val fileSaver: FileSaver,
) {

    private val maxImageBytes: Long = 5_242_880L // 5 * 1024 * 1024
    private val client = HttpClient()

    /**
     * Download the image at [url], stream it into the provided writer and return the local path.
     * Throws on any error.
     */
    suspend fun fetchAndStore(url: String): String {
        if (url.isBlank()) throw IllegalArgumentException("url is blank")

        // Basic URL validation
        try {
            Url(url)
        } catch (t: Throwable) {
            throw IllegalArgumentException("invalid url: $url", t)
        }

        // Perform GET request
        val response = client.get(url)

        // Validate HTTP status
        val status = response.status
        if (!status.isSuccess()) {
            throw RuntimeException("HTTP ${status.value} when fetching image: $url")
        }

        // Validate content type is an image
        val contentTypeHeader = response.headers[HttpHeaders.ContentType]
        if (contentTypeHeader == null || !contentTypeHeader.startsWith("image/")) {
            throw RuntimeException("unexpected content-type: $contentTypeHeader")
        }

        // If server provided content-length, enforce max size early
        val contentLengthHeader = response.headers[HttpHeaders.ContentLength]
        if (contentLengthHeader != null) {
            val length = contentLengthHeader.toLongOrNull()
            if (length != null && length > maxImageBytes) {
                throw RuntimeException("image size $length exceeds limit $maxImageBytes")
            }
        }

        // Build filename early
        val fileName = buildFileName(url, contentTypeHeader)

        return fileSaver.save(fileName, response.bodyAsBytes())
    }

    /**
     * Construct a filename from URL-safe base64 of the URL and try to pick an extension
     * based on the Content-Type header (if present) or the URL path as a fallback.
     */
    private fun buildFileName(url: String, contentTypeHeader: String?): String {
        // Create a short, deterministic id based on SHA-256 of the URL and encode it URL-safe
        val fullHashBase64 = url.encodeUtf8().sha256().base64()
        val shortHash = fullHashBase64.replace('+', '-').replace('/', '_').replace("=", "").take(24)

        var extension: String? = null
        if (!contentTypeHeader.isNullOrBlank()) {
            val contentType = contentTypeHeader.substringBefore(';').trim().lowercase()
            extension = when (contentType) {
                "image/jpeg", "image/jpg" -> "jpg"
                "image/png" -> "png"
                else -> null
            }
        }

        return if (extension != null) "$shortHash.$extension" else shortHash
    }
}
